<template>
  <!--
    Issue #29 — Rediseño de notas: cards + galería plantillas + editor visual (tareas 8.1–8.3)
    Issue #30 — Drag-and-drop + contador + vista previa (tareas 8.4–8.6)
  -->
  <div class="fin-notes">
    <!-- ── Encabezado ──────────────────────────────────────────────────────── -->
    <div class="notes-header">
      <div>
        <h3>Notas a los Estados Financieros</h3>
        <p class="notes-desc">
          Revelaciones obligatorias según NIC 1 párrafo 112 y NIIF para PYMES Sección 8.
        </p>
      </div>
      <div class="header-actions">
        <Button
          label="Vista previa"
          icon="pi pi-eye"
          severity="secondary"
          outlined
          size="small"
          aria-label="Ver vista previa del informe de notas"
          @click="showPreview = true"
        />
        <Button
          label="Plantillas"
          icon="pi pi-copy"
          severity="secondary"
          outlined
          size="small"
          @click="showTemplates = true"
        />
        <Button label="Nueva nota" icon="pi pi-plus" size="small" @click="openCreate" />
      </div>
    </div>

    <!-- ── Contador de progreso (Issue #30 tarea 8.5) ─────────────────────── -->
    <NotesCounter :notes="notes" />

    <!-- ── Lista de notas ──────────────────────────────────────────────────── -->
    <div v-if="loading" class="loading-msg" role="status">
      <i class="pi pi-spin pi-spinner" aria-hidden="true" /> Cargando notas...
    </div>

    <div v-else-if="notes.length === 0" class="empty-notes" role="status">
      <i class="pi pi-file-edit" style="font-size:2rem;color:#d1d5db" aria-hidden="true" />
      <p>No hay notas para este documento.</p>
      <p class="empty-hint">Las notas son parte integral de los estados financieros y son obligatorias según NIIF.</p>
      <div class="empty-actions">
        <Button label="Usar plantillas" icon="pi pi-copy" outlined size="small" @click="showTemplates = true" />
        <Button label="Crear nota" icon="pi pi-plus" size="small" @click="openCreate" />
      </div>
    </div>

    <!-- ── Cards con drag-and-drop (Issue #30 tarea 8.4) ─────────────────── -->
    <div v-else class="notes-cards" aria-label="Lista de notas — arrastrar para reordenar">
      <div
        v-for="(note, idx) in notes"
        :key="note.id"
        class="note-card"
        :class="{ 'note-card--dragging': dragIdx === idx, 'note-card--over': dropIdx === idx && dragIdx !== idx }"
        draggable="true"
        :aria-label="`Nota ${note.note_number}: ${note.title}. Arrastra para reordenar.`"
        @dragstart="onDragStart(idx)"
        @dragover.prevent="onDragOver(idx)"
        @drop.prevent="onDrop(idx)"
        @dragend="onDragEnd"
      >
        <!-- Tipo icono -->
        <div class="note-type-strip" :style="{ background: typeColor(note.note_type) }" aria-hidden="true" />

        <!-- Contenido -->
        <div class="note-body">
          <div class="note-top">
            <div class="note-meta">
              <span class="note-number" aria-label="Número de nota">Nota {{ note.note_number }}</span>
              <span class="note-type-tag" :style="{ background: typeColor(note.note_type) + '22', color: typeColor(note.note_type) }">
                {{ typeLabel(note.note_type) }}
              </span>
              <Tag v-if="note.norm_reference" :value="note.norm_reference" severity="info" size="small" />
            </div>

            <!-- Drag handle + acciones -->
            <div class="note-actions" role="group" :aria-label="`Acciones para nota ${note.note_number}`">
              <span class="drag-handle" title="Arrastra para reordenar" aria-hidden="true">⠿</span>
              <Button icon="pi pi-pencil" text rounded size="small" aria-label="Editar nota" @click="openEdit(note)" />
              <Button icon="pi pi-trash" text rounded size="small" severity="danger" aria-label="Eliminar nota" @click="confirmDelete(note)" />
            </div>
          </div>

          <h4 class="note-title">{{ note.title }}</h4>

          <!-- Vista expandida/colapsada -->
          <p class="note-preview" :class="{ expanded: expandedNotes.has(note.id) }">
            {{ note.content }}
          </p>

          <button
            v-if="note.content.length > 180"
            type="button"
            class="note-expand-btn"
            :aria-expanded="expandedNotes.has(note.id)"
            :aria-controls="`note-content-${note.id}`"
            @click="toggleExpand(note.id)"
          >
            {{ expandedNotes.has(note.id) ? 'Ver menos ▲' : 'Ver más ▼' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Dialog crear/editar nota con editor visual (Issue #29 tarea 8.3) ─ -->
    <Dialog
      v-model:visible="showForm"
      :header="editingNote ? `Editar Nota ${editingNote.note_number}` : 'Nueva Nota'"
      modal
      :style="{ width: '680px' }"
      :breakpoints="{ '768px': '95vw' }"
    >
      <div class="note-form">
        <div class="field-row">
          <div class="field">
            <label for="note-number-input">Número de nota <span class="required" aria-hidden="true">*</span></label>
            <InputNumber id="note-number-input" v-model="form.note_number" :min="1" :max="99" class="w-full" />
          </div>
          <div class="field">
            <label for="note-type-input">Tipo</label>
            <Select
              id="note-type-input"
              v-model="form.note_type"
              :options="noteTypes"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            />
          </div>
        </div>

        <div class="field">
          <label for="note-title-input">Título <span class="required" aria-hidden="true">*</span></label>
          <InputText id="note-title-input" v-model="form.title" placeholder="Ej: Políticas Contables Significativas" class="w-full" />
          <small v-if="errors.title" class="p-error">{{ errors.title }}</small>
        </div>

        <div class="field">
          <label for="note-norm-input">Referencia normativa</label>
          <InputText id="note-norm-input" v-model="form.norm_reference" placeholder="Ej: NIC 1.112, NIIF PYMES S8" class="w-full" />
        </div>

        <!-- Editor visual con barra de formato -->
        <div class="field">
          <label id="content-label">Contenido <span class="required" aria-hidden="true">*</span></label>

          <!-- Barra de formato -->
          <div class="editor-toolbar" role="toolbar" aria-label="Barra de formato de texto">
            <button type="button" class="fmt-btn" title="Negrita" aria-label="Insertar negrita" @click="insertFormat('**','**')"><b>B</b></button>
            <button type="button" class="fmt-btn" title="Cursiva" aria-label="Insertar cursiva" @click="insertFormat('_','_')"><i>I</i></button>
            <button type="button" class="fmt-btn" title="Lista con viñetas" aria-label="Insertar lista" @click="insertFormat('\n• ','')">≡</button>
            <button type="button" class="fmt-btn" title="Guión de separación" aria-label="Insertar separador" @click="insertFormat('\n---\n','')">―</button>
            <div class="toolbar-sep" aria-hidden="true" />
            <button type="button" class="fmt-btn fmt-btn--clear" title="Borrar contenido" aria-label="Borrar contenido del editor" @click="form.content = ''">✕</button>
          </div>

          <Textarea
            ref="textareaRef"
            id="note-content-editor"
            v-model="form.content"
            placeholder="Describe la política contable, contingencia o revelación..."
            rows="8"
            class="w-full editor-textarea"
            autoResize
            aria-labelledby="content-label"
          />
          <small v-if="errors.content" class="p-error">{{ errors.content }}</small>
          <small class="field-hint">{{ form.content.length.toLocaleString() }} caracteres</small>
        </div>
      </div>

      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showForm = false" />
        <Button
          :label="editingNote ? 'Guardar cambios' : 'Crear nota'"
          icon="pi pi-check"
          :loading="saving"
          @click="saveNote"
        />
      </template>
    </Dialog>

    <!-- ── Dialog galería de plantillas (Issue #29 tarea 8.2) ─────────────── -->
    <Dialog
      v-model:visible="showTemplates"
      header="Galería de Plantillas"
      modal
      :style="{ width: '740px' }"
      :breakpoints="{ '768px': '95vw' }"
    >
      <!-- Buscador de plantillas -->
      <div class="tmpl-search-row">
        <label for="tmpl-search" class="sr-only">Buscar plantillas</label>
        <i class="pi pi-search tmpl-search-icon" aria-hidden="true" />
        <input
          id="tmpl-search"
          v-model="tmplQuery"
          type="search"
          placeholder="Buscar plantilla…"
          class="tmpl-search-input"
        />
      </div>

      <!-- Grid de plantillas -->
      <div v-if="filteredTemplates.length" class="templates-grid">
        <div
          v-for="(tmpl, key) in filteredTemplatesObj"
          :key="key"
          class="template-card"
          role="button"
          tabindex="0"
          :aria-label="`Usar plantilla: ${tmpl.title}`"
          @click="applyTemplate(key, tmpl)"
          @keydown.enter="applyTemplate(key, tmpl)"
        >
          <!-- Icono grande por tipo -->
          <div class="tmpl-icon" aria-hidden="true">{{ typeIcon(key) }}</div>
          <div class="tmpl-header">
            <span class="tmpl-title">{{ tmpl.title }}</span>
            <Tag v-if="tmpl.norm_reference" :value="tmpl.norm_reference" severity="info" size="small" />
          </div>
          <p class="tmpl-preview">{{ tmpl.content_preview }}</p>
          <div class="tmpl-footer">
            <span class="tmpl-type-chip">{{ typeLabel(key) }}</span>
            <Button label="Usar plantilla" icon="pi pi-plus" text size="small" />
          </div>
        </div>
      </div>

      <div v-else class="tmpl-empty" role="status">
        <span aria-hidden="true">🔍</span>
        <p>No se encontraron plantillas para "<strong>{{ tmplQuery }}</strong>".</p>
      </div>
    </Dialog>

    <!-- ── Confirm delete ─────────────────────────────────────────────────── -->
    <Dialog v-model:visible="showDeleteConfirm" header="Eliminar nota" modal :style="{ width: '380px' }">
      <p>¿Eliminar la <strong>Nota {{ deletingNote?.note_number }} — {{ deletingNote?.title }}</strong>?</p>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showDeleteConfirm = false" />
        <Button label="Eliminar" severity="danger" :loading="deleting" @click="executeDelete" />
      </template>
    </Dialog>

    <!-- ── Vista previa (Issue #30 tarea 8.6) ────────────────────────────── -->
    <NoteReportPreview
      :visible="showPreview"
      :notes="previewNotes"
      @close="showPreview = false"
    />

    <Toast />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import Select from 'primevue/select'
import Toast from 'primevue/toast'
import api from '@/services/api'
import NotesCounter from './NotesCounter.vue'
import NoteReportPreview from './NoteReportPreview.vue'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Note {
  id: number
  note_number: number
  title: string
  content: string
  note_type: string
  norm_reference: string | null
}

interface Template {
  title: string
  content_preview?: string
  norm_reference?: string
  content?: string
}

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps<{ documentId: number }>()

// ── State ─────────────────────────────────────────────────────────────────────

const toast              = useToast()
const notes              = ref<Note[]>([])
const templates          = ref<Record<string, Template>>({})
const loading            = ref(false)
const showForm           = ref(false)
const showTemplates      = ref(false)
const showDeleteConfirm  = ref(false)
const showPreview        = ref(false)
const editingNote        = ref<Note | null>(null)
const deletingNote       = ref<Note | null>(null)
const saving             = ref(false)
const deleting           = ref(false)
const errors             = ref<Record<string, string>>({})
const tmplQuery          = ref('')
const expandedNotes      = ref<Set<number>>(new Set())
const textareaRef        = ref<InstanceType<typeof Textarea> | null>(null)

// Drag-and-drop state (Issue #30)
const dragIdx = ref<number | null>(null)
const dropIdx = ref<number | null>(null)

// ── Tipos de nota ─────────────────────────────────────────────────────────────

const noteTypes = [
  { label: 'General',                   value: 'general' },
  { label: 'Políticas contables',        value: 'politicas_contables' },
  { label: 'Efectivo y equivalentes',    value: 'efectivo' },
  { label: 'Cuentas por cobrar',         value: 'cuentas_cobrar' },
  { label: 'Inventarios',               value: 'inventarios' },
  { label: 'Propiedad, planta y equipo', value: 'propiedad_planta' },
  { label: 'Contingencias',             value: 'contingencias' },
  { label: 'Hechos posteriores',         value: 'hechos_posteriores' },
]

// ── Formulario ────────────────────────────────────────────────────────────────

const form = ref({
  note_number:   1,
  title:         '',
  content:       '',
  note_type:     'general',
  norm_reference: '',
})

// ── Helpers de tipo ───────────────────────────────────────────────────────────

function typeLabel(type: string): string {
  return noteTypes.find(t => t.value === type)?.label ?? type
}

function typeColor(type: string): string {
  const map: Record<string, string> = {
    general:             '#6366f1',
    politicas_contables: '#0ea5e9',
    efectivo:            '#22c55e',
    cuentas_cobrar:      '#f59e0b',
    inventarios:         '#8b5cf6',
    propiedad_planta:    '#ec4899',
    contingencias:       '#ef4444',
    hechos_posteriores:  '#64748b',
  }
  return map[type] ?? '#9ca3af'
}

function typeIcon(type: string): string {
  const icons: Record<string, string> = {
    general:             '📄',
    politicas_contables: '📋',
    efectivo:            '💵',
    cuentas_cobrar:      '🧾',
    inventarios:         '📦',
    propiedad_planta:    '🏭',
    contingencias:       '⚠️',
    hechos_posteriores:  '📅',
  }
  return icons[type] ?? '📄'
}

// ── Filtro de plantillas ──────────────────────────────────────────────────────

const filteredTemplatesObj = computed<Record<string, Template>>(() => {
  if (!tmplQuery.value.trim()) return templates.value
  const q = tmplQuery.value.toLowerCase()
  return Object.fromEntries(
    Object.entries(templates.value).filter(([k, v]) =>
      v.title.toLowerCase().includes(q) ||
      k.toLowerCase().includes(q) ||
      (v.content_preview ?? '').toLowerCase().includes(q),
    ),
  )
})

const filteredTemplates = computed(() => Object.keys(filteredTemplatesObj.value))

const previewNotes = computed(() =>
  notes.value.map((note) => ({
    ...note,
    norm_reference: note.norm_reference ?? undefined,
  })),
)

// ── Expandir notas ────────────────────────────────────────────────────────────

function toggleExpand(id: number) {
  if (expandedNotes.value.has(id)) expandedNotes.value.delete(id)
  else expandedNotes.value.add(id)
}

// ── Insertar formato en editor ────────────────────────────────────────────────

function insertFormat(before: string, after: string) {
  const el = (textareaRef.value as unknown as { $el?: HTMLTextAreaElement })
    ?.$el?.querySelector('textarea') ??
    document.getElementById('note-content-editor') as HTMLTextAreaElement | null

  if (!el) { form.value.content += before + after; return }

  const start = el.selectionStart
  const end   = el.selectionEnd
  const sel   = form.value.content.slice(start, end)
  form.value.content =
    form.value.content.slice(0, start) +
    before + sel + after +
    form.value.content.slice(end)

  setTimeout(() => {
    el.focus()
    el.setSelectionRange(start + before.length, start + before.length + sel.length)
  }, 0)
}

// ── Drag-and-drop ─────────────────────────────────────────────────────────────

function onDragStart(idx: number) {
  dragIdx.value = idx
}

function onDragOver(idx: number) {
  dropIdx.value = idx
}

function onDrop(idx: number) {
  if (dragIdx.value === null || dragIdx.value === idx) return
  const arr = [...notes.value]
  const [moved] = arr.splice(dragIdx.value, 1)
  arr.splice(idx, 0, moved)
  // Re-numerar
  notes.value = arr.map((n, i) => ({ ...n, note_number: i + 1 }))
  dragIdx.value = null
  dropIdx.value = null
}

function onDragEnd() {
  dragIdx.value = null
  dropIdx.value = null
}

// ── Carga de datos ────────────────────────────────────────────────────────────

const loadNotes = async () => {
  loading.value = true
  try {
    const res = await api.get(`/financial/notes/${props.documentId}`)
    notes.value = res.data
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const loadTemplates = async () => {
  try {
    const res = await api.get('/financial/notes/templates')
    templates.value = res.data
  } catch (e) {
    console.error(e)
  }
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

const openCreate = () => {
  editingNote.value = null
  form.value = {
    note_number: notes.value.length > 0
      ? Math.max(...notes.value.map(n => n.note_number)) + 1
      : 1,
    title:         '',
    content:       '',
    note_type:     'general',
    norm_reference: '',
  }
  errors.value = {}
  showForm.value = true
}

const openEdit = (note: Note) => {
  editingNote.value = note
  form.value = {
    note_number:    note.note_number,
    title:          note.title,
    content:        note.content,
    note_type:      note.note_type,
    norm_reference: note.norm_reference ?? '',
  }
  errors.value = {}
  showForm.value = true
}

const applyTemplate = async (key: string, _tmpl: Template) => {
  try {
    const res = await api.get(`/financial/notes/templates/${key}`)
    const full: Template & { content: string } = res.data
    form.value = {
      note_number: notes.value.length > 0
        ? Math.max(...notes.value.map(n => n.note_number)) + 1
        : 1,
      title:          full.title,
      content:        full.content,
      note_type:      key,
      norm_reference: full.norm_reference ?? '',
    }
    editingNote.value = null
    errors.value = {}
    showTemplates.value = false
    showForm.value = true
  } catch (e) {
    console.error(e)
  }
}

const validate = () => {
  errors.value = {}
  if (!form.value.title.trim())   errors.value.title   = 'El título es obligatorio'
  if (!form.value.content.trim()) errors.value.content = 'El contenido es obligatorio'
  return Object.keys(errors.value).length === 0
}

const saveNote = async () => {
  if (!validate()) return
  saving.value = true
  try {
    const payload = {
      note_number:    form.value.note_number,
      title:          form.value.title.trim(),
      content:        form.value.content.trim(),
      note_type:      form.value.note_type,
      norm_reference: form.value.norm_reference.trim() || null,
    }
    if (editingNote.value) {
      await api.put(`/financial/notes/${props.documentId}/${editingNote.value.id}`, payload)
      toast.add({ severity: 'success', summary: 'Actualizada', detail: 'Nota actualizada', life: 2500 })
    } else {
      await api.post(`/financial/notes/${props.documentId}`, payload)
      toast.add({ severity: 'success', summary: 'Creada', detail: 'Nota creada correctamente', life: 2500 })
    }
    showForm.value = false
    await loadNotes()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { detail?: string } }; message?: string }
    toast.add({ severity: 'error', summary: 'Error', detail: err.response?.data?.detail ?? err.message, life: 3000 })
  } finally {
    saving.value = false
  }
}

const confirmDelete = (note: Note) => {
  deletingNote.value = note
  showDeleteConfirm.value = true
}

const executeDelete = async () => {
  deleting.value = true
  try {
    await api.delete(`/financial/notes/${props.documentId}/${deletingNote.value!.id}`)
    showDeleteConfirm.value = false
    await loadNotes()
    toast.add({ severity: 'success', summary: 'Eliminada', detail: 'Nota eliminada', life: 2500 })
  } catch {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 })
  } finally {
    deleting.value = false
  }
}

onMounted(() => {
  loadNotes()
  loadTemplates()
})
</script>

<style scoped>
/* ── Contenedor ─────────────────────────────────────────────────────────────── */
.fin-notes { display: flex; flex-direction: column; gap: 1.25rem; }

/* ── Encabezado ─────────────────────────────────────────────────────────────── */
.notes-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.notes-header h3 { margin: 0; color: var(--p-text-color, #1e293b); font-size: 1rem; }
.notes-desc { margin: 0.25rem 0 0; font-size: 0.82rem; color: var(--p-text-muted-color, #6b7280); }
.header-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

/* ── Estados vacíos / carga ─────────────────────────────────────────────────── */
.loading-msg, .empty-notes {
  text-align: center; padding: 2rem;
  color: var(--p-text-muted-color, #9ca3af);
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
}
.empty-hint { font-size: 0.8rem; max-width: 400px; }
.empty-actions { display: flex; gap: 0.5rem; margin-top: 0.5rem; }

/* ── Cards de notas ─────────────────────────────────────────────────────────── */
.notes-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.875rem;
}

.note-card {
  display: flex;
  background: var(--p-surface-0, #fff);
  border: 1.5px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  cursor: grab;
}

.note-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.note-card:active { cursor: grabbing; }

.note-card--dragging {
  opacity: 0.5;
  border: 2px dashed #6366f1;
}

.note-card--over {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.2);
}

/* Franja de color del tipo */
.note-type-strip { width: 4px; flex-shrink: 0; }

.note-body { flex: 1; padding: 0.875rem; display: flex; flex-direction: column; gap: 0.4rem; }

.note-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.note-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }

.note-number {
  font-size: 0.7rem;
  font-weight: 700;
  color: #6366f1;
  background: #eff6ff;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.note-type-tag {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}

.drag-handle {
  font-size: 1.1rem;
  color: var(--p-text-muted-color, #d1d5db);
  cursor: grab;
  padding: 0 0.25rem;
  user-select: none;
}

.note-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--p-text-color, #1e293b);
  line-height: 1.3;
}

.note-preview {
  margin: 0;
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #374151);
  line-height: 1.5;
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.note-preview.expanded {
  -webkit-line-clamp: unset;
  display: block;
}

.note-expand-btn {
  background: none;
  border: none;
  font-size: 0.7rem;
  color: #6366f1;
  cursor: pointer;
  padding: 0;
  font-weight: 600;
  text-align: left;
}

/* ── Editor WYSIWYG simple ──────────────────────────────────────────────────── */
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.6rem;
  background: var(--p-surface-100, #f3f4f6);
  border: 1.5px solid var(--p-surface-300, #d1d5db);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
}

.fmt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--p-text-color, #374151);
  transition: background 0.1s ease;
}
.fmt-btn:hover { background: var(--p-surface-200, #e5e7eb); }
.fmt-btn--clear { color: #ef4444; }

.toolbar-sep {
  width: 1px;
  height: 18px;
  background: var(--p-surface-300, #d1d5db);
  margin: 0 0.15rem;
}

.editor-textarea :deep(textarea) {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  font-family: 'Courier New', monospace;
  font-size: 0.82rem;
  line-height: 1.6;
}

/* ── Galería de plantillas ──────────────────────────────────────────────────── */
.tmpl-search-row {
  position: relative;
  margin-bottom: 1rem;
}

.tmpl-search-icon {
  position: absolute;
  left: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 0.82rem;
  pointer-events: none;
}

.tmpl-search-input {
  width: 100%;
  padding: 0.55rem 1rem 0.55rem 2.3rem;
  border: 1.5px solid var(--p-surface-300, #d1d5db);
  border-radius: 10px;
  font-size: 0.82rem;
  background: var(--p-surface-0, #fff);
  color: var(--p-text-color, #111827);
  outline: none;
}
.tmpl-search-input:focus { border-color: #6366f1; }

.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem;
}

.template-card {
  border: 1.5px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.template-card:hover { border-color: #6366f1; background: #fafafe; }
.template-card:focus { outline: 2px solid #6366f1; outline-offset: 2px; }

.tmpl-icon { font-size: 2rem; line-height: 1; }

.tmpl-header { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.tmpl-title { font-weight: 700; font-size: 0.85rem; color: var(--p-text-color, #1e293b); }
.tmpl-preview { font-size: 0.75rem; color: var(--p-text-muted-color, #6b7280); margin: 0; line-height: 1.4; flex: 1; }

.tmpl-footer { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-top: 0.2rem; }

.tmpl-type-chip {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  background: var(--p-surface-100, #f3f4f6);
  border-radius: 999px;
  color: var(--p-text-muted-color, #6b7280);
}

.tmpl-empty {
  text-align: center;
  padding: 2rem;
  color: var(--p-text-muted-color, #9ca3af);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

/* ── Form ───────────────────────────────────────────────────────────────────── */
.note-form { display: flex; flex-direction: column; gap: 1rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.875rem; color: var(--p-text-color, #374151); }
.field-row { display: grid; grid-template-columns: 1fr 2fr; gap: 1rem; }
.required { color: #ef4444; }
.field-hint { font-size: 0.75rem; color: var(--p-text-muted-color, #9ca3af); }
.w-full { width: 100%; }

/* ── Accesibilidad ──────────────────────────────────────────────────────────── */
.sr-only {
  position: absolute; width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

/* ── Modo oscuro ────────────────────────────────────────────────────────────── */
:root[data-theme='dark'] .note-card,
.dark .note-card {
  background: var(--p-surface-700, #374151);
  border-color: var(--p-surface-600, #4b5563);
}

:root[data-theme='dark'] .template-card:hover,
.dark .template-card:hover {
  background: var(--p-surface-700, #374151);
}

:root[data-theme='dark'] .editor-toolbar,
.dark .editor-toolbar {
  background: var(--p-surface-700, #374151);
  border-color: var(--p-surface-600, #4b5563);
}

:root[data-theme='dark'] .tmpl-search-input,
.dark .tmpl-search-input {
  background: var(--p-surface-700, #374151);
  border-color: var(--p-surface-600, #4b5563);
  color: var(--p-text-color, #f9fafb);
}
</style>
